// component.jsx — CardResultsComponent (vigente + histórico, mejor contraste)
import { useMemo } from "preact/hooks";
import { useCardResults } from "./store";
import {
  Box, Typography, List, ListItem, ListItemText, ListItemIcon, Chip,
  Accordion, AccordionSummary, AccordionDetails, Card, CardContent, Stack
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentsIcon from "@mui/icons-material/Payments";
import BuildIcon from "@mui/icons-material/Build";
import CodeIcon from "@mui/icons-material/Code";
import DoneIcon from "@mui/icons-material/Done";
import HistoryIcon from "@mui/icons-material/History";
import SummarizeIcon from "@mui/icons-material/Summarize";
const StyledCardResults = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  position: "relative",
  borderRadius: 8,
  overflow: "hidden",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const SummaryHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SummaryRow = styled(Box)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  return {
    minHeight: 48,
    backgroundColor: isDark ? alpha(theme.palette.grey[800], 0.8) : theme.palette.grey[100],
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25, 1.5),
  };
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  "&:before": { display: "none" },
}));

const StyledSummary = styled(AccordionSummary)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  return {
    minHeight: 44,
    backgroundColor: isDark ? alpha(theme.palette.grey[900], 0.6) : alpha(theme.palette.grey[50], 0.9),
    "& .MuiAccordionSummary-content": {
      margin: theme.spacing(0.5, 0),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      color: theme.palette.text.primary,
    },
    "& .MuiSvgIcon-root": { color: theme.palette.text.secondary },
  };
});

const ResultItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  alignItems: "flex-start",
  "&:not(:last-child)": { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}` },
}));

const PriceChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  fontWeight: 600,
}));

function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" });
}

export const CardResultsComponent = () => {
  const { current, history, totals } = useCardResults();

  const fmtCurrency = useMemo(
    () => (n, c) => new Intl.NumberFormat("es-MX", { style: "currency", currency: c || "MXN" }).format(n || 0),
    []
  );

  return (
    <StyledCardResults elevation={1}>
      {/* Header primary */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 1.5,
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h6"
          color="primary.contrastText"
          sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}
        >
          <SummarizeIcon fontSize="small" sx={{ color: "primary.contrastText" }} />
          Resultados
        </Typography>

        <Typography variant="body2" color="primary.contrastText" sx={{ opacity: 0.85 }}>
          Vigente e histórico
        </Typography>
      </Box>


      <CardContent>
        {!current ? (
          <Typography variant="body2" color="text.secondary">
            No hay resultados disponibles para esta cotización.
          </Typography>
        ) : (
          <>
            {/* Resultado vigente */}
            <SummaryRow>
              <SummaryHeader>
                <DoneIcon color="success" fontSize="small" />
                <Typography variant="subtitle1" color="primary" fontWeight={600}>
                  {current.notes || "Sin notas"}
                </Typography>
              </SummaryHeader>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
                {!!current.availableStartDate && (
                  <Chip
                    size="small"
                    icon={<EventAvailableIcon />}
                    label={`Fecha de arranque: ${fmtDate(current.availableStartDate)}`}
                    variant="outlined"
                    color="primary"
                  />
                )}
                {!!current.estimatedTimeDays && (
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon />}
                    label={`${current.estimatedTimeDays} días`}
                    variant="outlined"
                    color="primary"
                  />
                )}
                {!!current.estimatedDevCost && (
                  <Chip
                    size="small"
                    icon={<CodeIcon />}
                    label={`Desarrollo: ${fmtCurrency(current.estimatedDevCost, current.currency)}`}
                    variant="outlined"
                    color="primary"
                  />
                )}
                {!!current.estimatedInfraCost && (
                  <Chip
                    size="small"
                    icon={<BuildIcon />}
                    label={`Infraestructura: ${fmtCurrency(current.estimatedInfraCost, current.currency)} / MES`}
                    variant="outlined"
                    color="primary"
                  />
                )}
                <PriceChip
                  size="small"
                  icon={<PaymentsIcon color="primary.contrastText" />}
                  label={`Total: ${fmtCurrency(current.costTotal, current.currency)}`}
                />
              </Stack>
            </SummaryRow>

            {/* Total global (vigente) */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: (t) => (t.palette.mode === "dark" ? alpha(t.palette.grey[800], 0.5) : t.palette.grey[50]),
                borderRadius: 1,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" color="success.main" fontWeight={600}>
                  Total: {fmtCurrency(totals.costTotal, totals.currency)}
                </Typography>
                {!!totals.days && (
                  <Chip size="small" icon={<AccessTimeIcon />} color="primary" label={`Días: ${totals.days}`} />
                )}
              </Stack>
            </Box>

            {/* Histórico */}
            {history.length > 0 && (
              <StyledAccordion sx={{ mt: 2 }}>
                <StyledSummary expandIcon={<ExpandMoreIcon />}>
                  <HistoryIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Histórico ({history.length})
                  </Typography>
                </StyledSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List dense sx={{ px: 2 }}>
                    {history.map((r, idx) => (
                      <ResultItem key={r.id || `${idx}-${r.createdAt || r.updatedAt || "row"}`}>
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.3 }}>
                          <HistoryIcon color="action" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          disableTypography
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.notes || "Sin notas"}
                            </Typography>
                          }
                          secondary={
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {!!r.availableStartDate && (
                                <Chip size="small" icon={<EventAvailableIcon />} label={fmtDate(r.availableStartDate)} variant="outlined" />
                              )}
                              {!!r.estimatedTimeDays && (
                                <Chip size="small" icon={<AccessTimeIcon />} label={`${r.estimatedTimeDays} días`} variant="outlined" />
                              )}
                              {!!r.estimatedDevCost && (
                                <Chip size="small" icon={<CodeIcon />} label={fmtCurrency(r.estimatedDevCost, r.currency)} variant="outlined" />
                              )}
                              {!!r.estimatedInfraCost && (
                                <Chip size="small" icon={<BuildIcon />} label={`${fmtCurrency(r.estimatedInfraCost, r.currency)} / MES`} variant="outlined" />
                              )}
                              <Chip size="small" icon={<PaymentsIcon color="primary.contrastText" />} label={fmtCurrency(r.costTotal, r.currency)} />
                            </Stack>
                          }
                        />
                      </ResultItem>
                    ))}
                  </List>
                </AccordionDetails>
              </StyledAccordion>
            )}
          </>
        )}
      </CardContent>
    </StyledCardResults>
  );
};
